import { useEffect, useMemo, useRef, useState } from "react";
import { properties as fallbackProperties, states } from "../data/properties";
import TableWithSlider from "../Components/TableWithSlider";
import ContactFormSection from "../Components/ContactFormSection";
import { getArticles, getNews, getPageSections, getPageTableColumns } from "../api/content";
import { getProperties } from "../api/properties";

const spotlightItems = [
  "High CAP opportunities with blue-chip tenancy",
  "Absolute NNN and NNN lease structures",
  "1031 exchange focused advisory",
  "Nationwide state-by-state inventory",
];

const fallbackArticles = [
  {
    id: "art-1",
    category: "NNN Insights",
    title: "How to Screen Tenant Credit Before You Buy",
    excerpt:
      "A practical due-diligence checklist for evaluating tenant quality, lease guarantees, and downside risks.",
    publishedAt: "2026-04-01",
  },
  {
    id: "art-2",
    category: "1031 Exchange",
    title: "Replacing Assets Under 1031 Deadlines",
    excerpt:
      "Key sequencing tactics for identification windows, lender alignment, and deal certainty.",
    publishedAt: "2026-03-24",
  },
  {
    id: "art-3",
    category: "CAP Rates",
    title: "Understanding CAP Rate Spread by Tenant Category",
    excerpt:
      "What yield differences can signal in relation to credit quality and location durability.",
    publishedAt: "2026-03-12",
  },
];

const newsItems = [
  {
    title: "US CAP Rate Watch",
    text: "Monthly briefings on movements in single-tenant retail, medical, and quick-service cap rates.",
  },
  {
    title: "NNN and Absolute NNN Structuring Updates",
    text: "Ongoing updates around lease structures, tenant obligations, and owner risk exposure.",
  },
  {
    title: "1031 Exchange Intelligence",
    text: "Regulatory developments and replacement-property timing guidance for exchange buyers.",
  },
];

const defaultHomepageContent = {
  hero: {
    headline: "Specialized in High CAP Investment Properties",
    subheadline:
      "We help investors acquire high CAP rate NNN and absolute NNN blue-chip properties leased to credit tenants with long-term leases and corporate guarantees.",
    background_image_url:
      "https://images.unsplash.com/photo-1460317442991-0ec209397118?auto=format&fit=crop&w=2000&q=80",
    background_image_alt: "Commercial city skyline",
  },
  propertyIntro: {
    summary_text:
      "The properties listed in the table below are NOT advertised on LoopNet. For details, tap on the property.",
    button_label: "Click for info",
    button_color: "#EBB866",
  },
  investorFocus: {
    eyebrow: "Investor Focus",
    title: "Blue-chip net-lease opportunities",
    description:
      "The platform is structured for investors looking for passive-income commercial deals with clearer lease risk profiles and long-term cash flow.",
    spotlight_items: spotlightItems,
  },
  insights: {
    articles_heading: "Latest Investor Articles",
    articles_subcopy: "Articles auto-load from the backend when published.",
    insights_title: "Investor Education Hub",
    insights_body:
      "Use this section for long-form investor insights, evergreen guidance, and market context that supports your homepage conversions.",
    market_briefing_title: "Market Briefing",
  },
};

const defaultTableColumns = [
  { column_key: "asking_price", label: "Asking Price", source_field: "askingPrice", is_visible: true, is_sortable: false },
  { column_key: "cap_rate", label: "CAP Rate", source_field: "capRate", is_visible: true, is_sortable: true },
  { column_key: "tenant", label: "Tenant", source_field: "tenant", is_visible: true, is_sortable: false },
  { column_key: "net_rent", label: "Net Rent", source_field: "netRent", is_visible: true, is_sortable: false },
  { column_key: "expires", label: "Expires", source_field: "expires", is_visible: true, is_sortable: false },
  { column_key: "rent_type", label: "Rent Type", source_field: "rentType", is_visible: true, is_sortable: false },
  { column_key: "landlord", label: "Landlord", source_field: "landlord", is_visible: true, is_sortable: false },
  { column_key: "built", label: "Built", source_field: "built", is_visible: true, is_sortable: false },
];

const landlordFromRentType = (rentType) => {
  if ((rentType || "").toLowerCase().includes("absolute")) return "Has zero responsibility";
  return "Has minimal responsibility";
};

const stateFilterLabel = (state) => (state === "All States" ? "Search by state" : `${state} Properties`);

const parsePercent = (value) => Number(String(value || "").replace(/[^0-9.-]/g, "")) || 0;

const normalizeProperty = (property, index) => {
  const propertyId = property.propertyId || property.id || `property-${index + 1}`;
  return {
    id: property.id || property.property_code || propertyId,
    propertyId,
    askingPrice:
      property.askingPrice ||
      property.price ||
      (property.asking_price != null ? `$ ${Number(property.asking_price).toLocaleString()}` : "N/A"),
    capRate:
      property.capRate ||
      (property.cap_rate != null ? `${Number(property.cap_rate).toFixed(2)}%` : "N/A"),
    netRent:
      property.netRent ||
      property.netRentPerMo ||
      (property.net_rent_monthly != null
        ? `$ ${Number(property.net_rent_monthly).toLocaleString()}/mo`
        : "N/A"),
    tenant: property.tenant || property.tenant_name || "N/A",
    expires:
      property.expires || property.leaseTerm || property.expires_label || property.lease_expires_at || "N/A",
    rentType: property.rentType || property.rent_type || "N/A",
    landlord:
      property.landlord ||
      property.landlord_responsibility ||
      landlordFromRentType(property.rentType || property.rent_type),
    built: property.built || property.yearBuilt || property.year_built || "N/A",
    state: property.state || "N/A",
    selectPropertyState: property.selectPropertyState || stateFilterLabel(property.state || "N/A"),
    status: property.status || "Active",
  };
};

function Homepage({ onNavigate }) {
  const [quickState, setQuickState] = useState("All States");
  const [selectedTableState, setSelectedTableState] = useState("All States");
  const [currentPage, setCurrentPage] = useState(1);
  const [sortConfig, setSortConfig] = useState({ key: "capRate", direction: "desc" });
  const [rowsPerPage, setRowsPerPage] = useState(20);
  const [tableProperties, setTableProperties] = useState(
    fallbackProperties.map((property, index) => normalizeProperty(property, index))
  );
  const [articles, setArticles] = useState(fallbackArticles);
  const [homepageContent, setHomepageContent] = useState(defaultHomepageContent);
  const [newsFeed, setNewsFeed] = useState(newsItems);
  const [tableColumns, setTableColumns] = useState(defaultTableColumns);
  const contactFormRef = useRef(null);

  useEffect(() => {
    let mounted = true;

    const loadProperties = async () => {
      try {
        const payload = await getProperties();
        const list = Array.isArray(payload) ? payload : payload?.data;
        if (!Array.isArray(list) || !mounted) return;
        setTableProperties(list.map((property, index) => normalizeProperty(property, index)));
        setSelectedTableState("All States");
        setCurrentPage(1);
      } catch {
        // fallback stays as local dummy data
      }
    };

    const loadArticles = async () => {
      try {
        const payload = await getArticles();
        const list = Array.isArray(payload) ? payload : payload?.data;
        if (!Array.isArray(list) || !mounted) return;

        setArticles(
          list.map((article, index) => ({
            id: article.id || `article-${index + 1}`,
            category: article.category || "Insights",
            title: article.title || "Untitled article",
            excerpt: article.excerpt || article.summary || "",
            publishedAt: article.published_at || article.publishedAt || article.date || "",
            url: article.external_url || article.url || article.link || "",
          }))
        );
      } catch {
        // fallback stays as local dummy data
      }
    };

    const loadSections = async () => {
      try {
        const payload = await getPageSections("home");
        if (!mounted || !Array.isArray(payload)) return;

        const sectionMap = Object.fromEntries(
          payload.map((section) => [section.section_key, section.payload_json || {}])
        );

        setHomepageContent((current) => ({
          ...current,
          hero: { ...current.hero, ...(sectionMap.hero || {}) },
          propertyIntro: { ...current.propertyIntro, ...(sectionMap.property_intro || {}) },
          investorFocus: { ...current.investorFocus, ...(sectionMap.investor_focus || {}) },
          insights: { ...current.insights, ...(sectionMap.insights || {}) },
        }));
      } catch {
        // local defaults stay in place
      }
    };

    const loadTableColumns = async () => {
      try {
        const payload = await getPageTableColumns("home");
        if (!mounted || !Array.isArray(payload) || !payload.length) return;
        setTableColumns(payload.filter((column) => column.is_visible !== false));
      } catch {
        // local defaults stay in place
      }
    };

    const loadNews = async () => {
      try {
        const payload = await getNews();
        if (!mounted || !Array.isArray(payload) || !payload.length) return;
        setNewsFeed(
          payload
            .filter((item) => item.is_visible !== false)
            .map((item) => ({
              title: item.title || "",
              text: item.body || "",
              linkUrl: item.link_url || "",
            }))
        );
      } catch {
        // local defaults stay in place
      }
    };

    loadProperties();
    loadSections();
    loadTableColumns();
    loadArticles();
    loadNews();

    return () => {
      mounted = false;
    };
  }, []);

  useEffect(() => {
    const mediaQuery = window.matchMedia("(max-width: 639px)");

    const updateRowsPerPage = (event) => {
      setRowsPerPage(event.matches ? 7 : 20);
      setCurrentPage(1);
    };

    updateRowsPerPage(mediaQuery);
    mediaQuery.addEventListener("change", updateRowsPerPage);

    return () => {
      mediaQuery.removeEventListener("change", updateRowsPerPage);
    };
  }, []);

  const summaryCount = useMemo(() => tableProperties.length, [tableProperties.length]);
  const filteredTableProperties = useMemo(() => {
    if (selectedTableState === "All States") return tableProperties;
    return tableProperties.filter((property) => property.state === selectedTableState);
  }, [selectedTableState, tableProperties]);
  const tableStateOptions = useMemo(
    () => ["All States", ...Array.from(new Set(tableProperties.map((property) => property.state))).sort()],
    [tableProperties]
  );

  

  const handlePropertySearch = (event) => {
    event.preventDefault();
    sessionStorage.setItem("preferredState", quickState);
    onNavigate("/properties");
  };

  const handleTableStateChange = (event) => {
    setSelectedTableState(event.target.value);
    setCurrentPage(1);
  };

  const handleCapRateSort = () => {
    setSortConfig((current) => {
      return {
        key: "capRate",
        direction: current.direction === "asc" ? "desc" : "asc",
      };
    });
  };




  const getColumnValue = (property, sourceField) => property?.[sourceField] ?? "N/A";

  const sortableColumns = useMemo(
    () => tableColumns.filter((column) => column.is_visible !== false),
    [tableColumns]
  );

  const sortedVisibleProperties = useMemo(() => {
    const activeColumn = sortableColumns.find((column) => column.source_field === sortConfig.key);
    if (!activeColumn?.is_sortable) return filteredTableProperties;

    return [...filteredTableProperties].sort((left, right) => {
      const leftValue = parsePercent(getColumnValue(left, activeColumn.source_field));
      const rightValue = parsePercent(getColumnValue(right, activeColumn.source_field));
      return sortConfig.direction === "asc" ? leftValue - rightValue : rightValue - leftValue;
    });
  }, [filteredTableProperties, sortConfig, sortableColumns]);

  const visiblePaginatedProperties = useMemo(() => {
    const start = (currentPage - 1) * rowsPerPage;
    return sortedVisibleProperties.slice(start, start + rowsPerPage);
  }, [currentPage, rowsPerPage, sortedVisibleProperties]);
  const totalPages = useMemo(
    () => Math.max(1, Math.ceil(sortedVisibleProperties.length / rowsPerPage)),
    [rowsPerPage, sortedVisibleProperties.length]
  );

  const renderCapRateHeader = () => {
    const indicator = sortConfig.direction === "asc" ? "▲" : "▼";

    return (
      <button
        type="button"
        className="flex items-center gap-2 text-xs uppercase tracking-[0.12em]"
        onClick={handleCapRateSort}
      >
        <span>{sortableColumns.find((column) => column.source_field === "capRate")?.label || "CAP Rate"}</span>
        <span className="text-[10px] text-cream-200/80">{indicator}</span>
      </button>
    );
  };

  const renderColumnHeader = (column) => {
    if (column.is_sortable && column.source_field === "capRate") {
      return renderCapRateHeader();
    }

    return column.label;
  };

  const scrollToContactForm = () => {
    contactFormRef.current?.scrollIntoView({ behavior: "smooth", block: "start" });
  };

  return (
    <div>
      <section className="relative overflow-hidden bg-obsidian-950">
        <img
          src={homepageContent.hero.background_image_url}
          alt={homepageContent.hero.background_image_alt}
          className="absolute inset-0 h-full w-full object-cover"
        />
        <div className="absolute inset-0 bg-gradient-to-r from-obsidian-950/85 via-obsidian-900/70 to-obsidian-800/55" />

        <div className="section-wrap relative z-10 flex items-start pt-4 pb-3 sm:min-h-[34vh] sm:items-center sm:pt-2 sm:pb-3 lg:min-h-[38vh] lg:pt-2 lg:pb-3">
          <div className="max-w-3xl animate-fade-up text-cream-50">
            <h1 className="mt-0 font-serif text-3xl leading-[1.02] sm:mt-1 sm:text-5xl lg:text-[4.5rem]">
              {homepageContent.hero.headline}
            </h1>
            <p className="mt-3 max-w-2xl pb-0 text-sm leading-6 text-cream-100/95 sm:mt-3 sm:text-base sm:leading-7">
              {homepageContent.hero.subheadline}
            </p>
            {/* <div className="mt-4 flex flex-wrap gap-2.5 sm:mt-5 sm:gap-3">
              <button
                onClick={() => onNavigate("/properties")}
                className="rounded-xl bg-gold-500 px-4 py-2.5 text-sm font-semibold text-obsidian-950 hover:bg-cream-300 sm:px-6 sm:py-3"
              >
                Explore Properties
              </button>
              <button
                onClick={() => onNavigate("/questionnaire")}
                className="rounded-xl border border-cream-100/40 px-4 py-2.5 text-sm font-semibold text-cream-50 hover:bg-cream-50/10 sm:px-6 sm:py-3"
              >
                Start Questionnaire
              </button>
            </div> */}
          </div>
        </div>
      </section>
      
      <section className="w-full bg-cream-50 py-5 sm:py-6">
        <div className="section-wrap">
          <div className="hidden flex-wrap items-end justify-between gap-4 sm:flex">
            <div>
              
              <p className="mt-1 text-sm text-obsidian-700">
                {summaryCount} listings shown. {homepageContent.propertyIntro.summary_text}

              </p>
            </div>
          </div>

          <div className="mt-2 flex items-end gap-2 sm:mt-4 sm:max-w-xl sm:gap-3">
            <label className="flex min-w-0 flex-1 flex-col gap-2 text-xs font-semibold uppercase tracking-[0.12em] text-obsidian-600">
              
              <select
                value={selectedTableState}
                onChange={handleTableStateChange}
                className="min-w-0 rounded-xl border border-obsidian-300 bg-white px-3 py-2.5 text-sm font-medium normal-case tracking-normal text-obsidian-900 sm:px-4 sm:py-3"
              >
                {tableStateOptions.map((state) => (
                  <option key={state} value={state}>
                    {stateFilterLabel(state)}
                  </option>
                ))}
              </select>
            </label>

            <button
              type="button"
              onClick={scrollToContactForm}
              className="shrink-0 whitespace-nowrap rounded-xl px-3 py-2.5 text-sm font-semibold text-obsidian-950 transition hover:bg-cream-200 sm:px-5 sm:py-3"
              style={{ backgroundColor: "#EBB866" }}
            >
              {homepageContent.propertyIntro.button_label}
            </button>
          </div>

          <div className="mt-4 overflow-scroll rounded-xl border border-obsidian-200 bg-blue shadow-blue sm:mt-8">
            <TableWithSlider
            >
              <table className="min-w-[920px] w-full text-left">
                <thead className="bg-obsidian-900 text-cream-50">
                  <tr>
                    {sortableColumns.map((column) => (
                      <th
                        key={column.column_key}
                        className="px-2 py-2 text-[11px] uppercase tracking-[0.08em] sm:px-4 sm:py-3 sm:text-xs sm:tracking-[0.12em]"
                      >
                        {renderColumnHeader(column)}
                      </th>
                    ))}
                  </tr>
                </thead>
                <tbody>
                  {visiblePaginatedProperties.map((property) => (
                    <tr
                      key={property.propertyId}
                      className="cursor-pointer border-t border-obsidian-100 text-sm text-obsidian-800 transition hover:bg-cream-50"
                      onClick={scrollToContactForm}
                    >
                      {sortableColumns.map((column) => (
                        <td key={`${property.propertyId}-${column.column_key}`} className="px-2 py-2 sm:px-4 sm:py-3">
                          {getColumnValue(property, column.source_field)}
                        </td>
                      ))}
                    </tr>
                  ))}
                </tbody>
              </table>
            </TableWithSlider>

            {/* <div
              ref={sliderScrollRef}
              className="homepage-table-slider overflow-x-auto border-t border-obsidian-200"
              onScroll={handleHomepageSliderScroll}
              aria-label="Table horizontal scroll"
            >
              <div ref={sliderInnerRef} className="homepage-table-slider-inner" />
            </div> */}
          </div>

          <div className="mt-4 flex flex-wrap items-center justify-between gap-3 sm:mt-5">
            <p className="text-sm text-obsidian-700">
              Showing {(currentPage - 1) * rowsPerPage + 1}-
              {Math.min(currentPage * rowsPerPage, sortedVisibleProperties.length)} of {sortedVisibleProperties.length} properties
            </p>

            <div className="flex flex-wrap gap-2">
              {Array.from({ length: totalPages }, (_, index) => index + 1).map((page) => (
                <button
                  key={page}
                  className={`rounded-lg px-4 py-2 text-sm font-semibold transition ${
                    currentPage === page
                      ? "bg-obsidian-900 text-cream-50"
                      : "bg-cream-100 text-obsidian-800 hover:bg-cream-200"
                  }`}
                  onClick={() => setCurrentPage(page)}
                >
                  {page}
                </button>
              ))}
            </div>
          </div>
        </div>
      </section>

      <section ref={contactFormRef} className="w-full bg-cream-100 py-8 sm:py-10">
        <div className="section-wrap scroll-mt-28">
          <ContactFormSection
            title="CONTACT FORM"
            
          />
        </div>
      </section>

      <section className="w-full bg-obsidian-900 py-6 text-cream-50 sm:py-10 lg:py-14">
        <div className="section-wrap grid gap-4 lg:grid-cols-2 lg:items-center lg:gap-8">
          <div>
            <p className="text-xs font-semibold uppercase tracking-[0.2em] text-cream-200/80">
              {homepageContent.investorFocus.eyebrow}
            </p>
            <h2 className="mt-2 font-serif text-4xl sm:text-5xl">{homepageContent.investorFocus.title}</h2>
            <p className="mt-3 text-sm leading-6 text-cream-100/90 sm:mt-4 sm:leading-7">
              {homepageContent.investorFocus.description}
            </p>
            <form className="mt-4 flex max-w-xl flex-col gap-3 sm:mt-6 sm:flex-row" onSubmit={handlePropertySearch}>
              <select
                className="w-full rounded-xl border border-obsidian-600 bg-obsidian-800 px-4 py-3 text-sm font-semibold text-cream-50"
                value={quickState}
                onChange={(event) => setQuickState(event.target.value)}
              >
                {states.map((state) => (
                  <option key={state} value={state}>
                    {state}
                  </option>
                ))}
              </select>
              <button
                type="submit"
                className="rounded-xl bg-obsidian-700 px-5 py-3 text-sm font-semibold text-cream-50 hover:bg-obsidian-600"
              >
                Search by State
              </button>
            </form>
          </div>

          <div className="hidden rounded-3xl bg-obsidian-800/70 p-6 lg:block">
            <p className="text-sm font-semibold uppercase tracking-[0.18em] text-gold-500">Spotlight</p>
            <ul className="mt-4 space-y-3 text-sm text-cream-100">
              {homepageContent.investorFocus.spotlight_items.map((item) => (
                <li key={item} className="rounded-xl border border-obsidian-600 bg-obsidian-900/70 px-4 py-3">
                  {item}
                </li>
              ))}
            </ul>
          </div>
        </div>
      </section>

      

      <section className="section-wrap space-y-14 py-12">
        <section>
          <div className="flex flex-wrap items-end justify-between gap-6">
            <div>
              <p className="text-xs font-semibold uppercase tracking-[0.2em] text-obsidian-600">Articles</p>
              <h2 className="mt-2 font-serif text-4xl text-obsidian-950">{homepageContent.insights.articles_heading}</h2>
              <p className="mt-2 text-sm text-obsidian-700">
                {homepageContent.insights.articles_subcopy}
              </p>
            </div>
          </div>

          <div className="mt-6 grid gap-4 md:grid-cols-2 xl:grid-cols-3">
            {articles.map((article) => (
              <article key={article.id} className="premium-panel p-6">
                <p className="text-xs font-semibold uppercase tracking-[0.16em] text-obsidian-600">
                  {article.category}
                </p>
                <h3 className="mt-2 font-serif text-3xl text-obsidian-950">{article.title}</h3>
                <p className="mt-2 text-sm leading-7 text-obsidian-700">{article.excerpt}</p>
                <p className="mt-4 text-xs font-semibold uppercase tracking-[0.12em] text-obsidian-500">
                  {article.publishedAt || "Date pending"}
                </p>
                {article.url ? (
                  <a
                    href={article.url}
                    target="_blank"
                    rel="noreferrer"
                    className="mt-4 inline-block rounded-xl bg-obsidian-700 px-4 py-2 text-xs font-semibold uppercase tracking-[0.12em] text-cream-50"
                  >
                    Read Article
                  </a>
                ) : null}
              </article>
            ))}
          </div>
        </section>

        <section className="grid gap-6 lg:grid-cols-3">
          <div className="lg:col-span-2">
            <p className="text-xs font-semibold uppercase tracking-[0.2em] text-obsidian-600">Insights</p>
            <h2 className="mt-2 font-serif text-4xl text-obsidian-950">{homepageContent.insights.insights_title}</h2>
            <article className="premium-panel mt-6 p-6">
              <p className="text-sm leading-7 text-obsidian-700">
                {homepageContent.insights.insights_body}
              </p>
            </article>
          </div>

          <aside className="premium-panel p-6">
            <p className="text-xs font-semibold uppercase tracking-[0.2em] text-obsidian-600">News</p>
            <h3 className="mt-2 font-serif text-3xl text-obsidian-950">{homepageContent.insights.market_briefing_title}</h3>
            <div className="mt-4 space-y-3">
              {newsFeed.map((item) => (
                <article key={item.title} className="rounded-xl border border-obsidian-200 bg-cream-50 p-4">
                  <p className="text-sm font-semibold text-obsidian-900">{item.title}</p>
                  <p className="mt-1 text-sm leading-6 text-obsidian-700">{item.text}</p>
                  <p className="mt-2 text-[11px] font-semibold uppercase tracking-[0.14em] text-obsidian-500">
                    [Add latest article content and source links here]
                  </p>
                </article>
              ))}
            </div>
          </aside>
        </section>
      </section>
    </div>
  );
}

export default Homepage;
